import {
  Caption1,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowDownload16Regular,
  ArrowSort16Regular,
  Dismiss24Regular,
  Document16Regular,
  Folder16Regular,
  Image16Regular,
  Link16Regular,
  MoreHorizontal16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { ListSearch, ListToolbar, useListToolbarStyles } from "@/components/list-toolbar";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { useDataTableStyles } from "@/components/data-table";
import { usePageAction } from "@/shell/page-action";
import { desktopDownloadClick } from "@/desktop/file-io";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { formatDate } from "@/lib/date-format";
import { formatBytes } from "@/lib/format";
import {
  FILE_SOURCE_LABEL,
  fileLibraryScale,
  fileSourceCaption,
  fileSourceTitle,
  type FileEntry,
  type FileKind,
  type FileKindFilter,
  type FileSortKey,
  type FileSourceFilter,
} from "./file-index";
import { FILE_MOCK_CASES, setFileMockCase, useFileMockCase, type FileMockCase } from "./file-mock";

const SORTS: Array<{ value: FileSortKey; label: string }> = [
  { value: "recent", label: "등록일" },
  { value: "name", label: "이름" },
  { value: "size", label: "크기" },
];

const SOURCES: Array<{ value: FileSourceFilter; label: string }> = [
  { value: "all", label: "전체" },
  { value: "task", label: FILE_SOURCE_LABEL.task },
  { value: "record", label: FILE_SOURCE_LABEL.record },
  { value: "document", label: FILE_SOURCE_LABEL.document },
  { value: "comment", label: FILE_SOURCE_LABEL.comment },
];

const KIND_EMPTY: Record<FileKind, { title: string; description: string }> = {
  image: { title: "이미지가 없습니다", description: "작업이나 이슈에 붙이면 여기에 모입니다." },
  file: { title: "파일이 없습니다", description: "작업이나 이슈에 붙이면 여기에 모입니다." },
  link: { title: "링크가 없습니다", description: "작업이나 이슈에 붙이면 여기에 모입니다." },
};

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  planFilter: { width: "180px" },
  body: {
    ...listBody,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
  },
  frame: { maxHeight: "100%" },
  row: {
    cursor: "pointer",
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active td": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  tableFixed: { tableLayout: "fixed", width: "100%", minWidth: "668px" },
  tableWithPlan: { minWidth: "796px" },
  /** 제목 열. 남는 폭을 흡수하고, 오른쪽에 세로선으로 지표 열과 가른다. */
  nameCol: {
    minWidth: "220px",
    width: "100%",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  /** 제목 오른쪽 열. 기본 셀(8px)보다 좌우 여백을 줄여 붙인다. */
  metric: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    whiteSpace: "nowrap",
  },
  nameInner: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, minWidth: 0 },
  sourceCol: { width: "200px", maxWidth: "200px", overflow: "hidden" },
  authorCol: { width: "56px" },
  planCol: { width: "128px", maxWidth: "128px", overflow: "hidden" },
  dateCol: { width: "104px" },
  sizeCol: { width: "88px" },
  muted: { color: tokens.colorNeutralForeground3 },
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  thumbButton: {
    display: "inline-flex",
    flexShrink: 0,
    width: "28px",
    height: "28px",
    padding: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: "hidden",
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  cardImage: {
    display: "block",
    width: "100%",
    aspectRatio: "4 / 3",
    padding: 0,
    border: "none",
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: "pointer",
    overflow: "hidden",
    ":hover": { backgroundColor: tokens.colorNeutralBackground3Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  cardImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  cardCaption: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  cardMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "2px",
    minWidth: 0,
    padding: tokens.spacingHorizontalS,
    border: "none",
    backgroundColor: "transparent",
    textAlign: "left",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  lightboxImg: {
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    borderRadius: tokens.borderRadiusMedium,
  },
});

export interface FileLibraryProps {
  /** 탭·출처·검색을 적용하기 전의 목록. 제목 옆 규모는 이 목록으로 적는다. */
  library: FileEntry[];
  entries: FileEntry[];
  scope: "plan" | "workspace";
  kind: FileKindFilter;
  source: FileSourceFilter;
  query: string;
  planId: string;
  sort: FileSortKey;
  onKind: (kind: FileKindFilter) => void;
  onSource: (source: FileSourceFilter) => void;
  onQuery: (query: string) => void;
  onPlan: (planId: string) => void;
  onSort: (sort: FileSortKey) => void;
  onOpen: (entry: FileEntry) => void;
}

export function FileLibrary({
  library,
  entries,
  scope,
  kind,
  source,
  query,
  planId,
  sort,
  onKind,
  onSource,
  onQuery,
  onPlan,
  onSort,
  onOpen,
}: FileLibraryProps) {
  const styles = useStyles();
  // 파일은 작업·이슈에서 붙인다. 상단 바의 Task·플랜 추가는 그대로 둔다.
  usePageAction(null);
  const toolbar = useListToolbarStyles();
  const table = useDataTableStyles();
  const [lightbox, setLightbox] = useState<FileEntry | null>(null);
  const planChoices = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of library) map.set(entry.projectId, entry.projectName || "이름 없는 플랜");
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [library]);
  const showPlanFilter = planChoices.length > 1;
  const showPlan = showPlanFilter && !planId;
  const onPlanRef = useRef(onPlan);
  onPlanRef.current = onPlan;
  useEffect(() => {
    if (planId && !planChoices.some((plan) => plan.id === planId)) onPlanRef.current("");
  }, [planId, planChoices]);
  const imageGrid = kind === "image";
  const narrowed = query.trim().length > 0 || source !== "all" || !!planId;
  const kindEmpty = kind === "all" ? null : KIND_EMPTY[kind];
  const emptyTitle = scope === "plan" ? "이 플랜에 올린 파일이 없습니다" : "이 워크스페이스에 올린 파일이 없습니다";
  const sortLabel = SORTS.find((item) => item.value === sort)?.label ?? "등록일";

  return (
    <div className={styles.page}>
      <ListToolbar
        title="파일"
        description={fileLibraryScale(library, planChoices.length > 1 ? "workspace" : scope)}
        titleAside={
          <FileMockMenu
            onPick={(next) => {
              setFileMockCase(next);
              onPlan("");
            }}
          />
        }
        sections={
          <TabList
            size="small"
            selectedValue={kind}
            aria-label="파일"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "all" || next === "image" || next === "file" || next === "link") onKind(next);
            }}
          >
            <Tab value="all" icon={<Folder16Regular />}>전체</Tab>
            <Tab value="image" icon={<Image16Regular />}>이미지</Tab>
            <Tab value="file" icon={<Document16Regular />}>파일</Tab>
            <Tab value="link" icon={<Link16Regular />}>링크</Tab>
          </TabList>
        }
        search={<ListSearch value={query} onChange={onQuery} />}
        filters={
          <>
            <SegmentedControl ariaLabel="파일 출처" value={source} onChange={onSource} options={SOURCES} />
            {showPlanFilter ? (
              <FuiFilterSelect
                className={styles.planFilter}
                label="플랜"
                ariaLabel="플랜"
                value={planChoices.some((plan) => plan.id === planId) ? planId : "all"}
                options={[{ value: "all", label: "모든 플랜" }, ...planChoices.map((plan) => ({ value: plan.id, label: plan.name }))]}
                onValueChange={(value) => onPlan(value === "all" ? "" : value)}
              />
            ) : null}
            <Menu
              checkedValues={{ sort: [sort] }}
              onCheckedValueChange={(_, data) => {
                const next = data.checkedItems[0];
                if (next === "recent" || next === "name" || next === "size") onSort(next);
              }}
            >
              <MenuTrigger disableButtonEnhancement>
                <FuiButton className={toolbar.nowrap} appearance="subtle" icon={<ArrowSort16Regular />}>
                  {sortLabel}
                </FuiButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList aria-label="정렬">
                  {SORTS.map((item) => (
                    <MenuItemRadio key={item.value} name="sort" value={item.value}>
                      {item.label}
                    </MenuItemRadio>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        }
      />
      <div className={styles.body}>
        {entries.length === 0 ? (
          library.length === 0 ? (
            <GhostEmptyState title={emptyTitle} description="작업이나 이슈를 열면 파일과 이미지를 붙일 수 있습니다." />
          ) : !narrowed && kindEmpty ? (
            <GhostEmptyState title={kindEmpty.title} description={kindEmpty.description} />
          ) : (
            <GhostEmptyState variant="search" title="조건에 맞는 파일이 없습니다" description="출처를 바꾸거나 검색어를 지워 보세요." />
          )
        ) : imageGrid ? (
          <div className={styles.grid}>
            {entries.map((entry) => (
              <ImageCard key={entry.key} entry={entry} showPlan={showPlan} onOpen={onOpen} onPreview={setLightbox} />
            ))}
          </div>
        ) : (
          <div className={mergeClasses(table.frame, styles.frame)}>
            <table className={mergeClasses(table.table, styles.tableFixed, showPlan && styles.tableWithPlan)}>
              <thead>
                <tr>
                  <th className={mergeClasses(table.th, styles.nameCol, styles.nameHead)}>파일</th>
                  <th className={mergeClasses(table.th, styles.metric, styles.sourceCol)}>출처</th>
                  <th className={mergeClasses(table.th, styles.metric, styles.authorCol)}>등록</th>
                  {showPlan && <th className={mergeClasses(table.th, styles.metric, styles.planCol)}>플랜</th>}
                  <th className={mergeClasses(table.th, styles.metric, table.num, styles.dateCol)}>등록일</th>
                  <th className={mergeClasses(table.th, styles.metric, table.num, styles.sizeCol)}>크기</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <FileRow key={entry.key} entry={entry} showPlan={showPlan} onOpen={onOpen} onPreview={setLightbox} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {lightbox && <ImageLightbox entry={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function FileMockMenu({ onPick }: { onPick: (id: FileMockCase) => void }) {
  const styles = useStyles();
  const scenario = useFileMockCase();
  return (
    <Menu
      checkedValues={{ case: scenario === "live" ? [] : [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "many" || next === "few" || next === "byPlan" || next === "empty") onPick(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {FILE_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
          <MenuDivider />
          <MenuItem onClick={() => onPick("live")}>초기화</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function FileRow({
  entry,
  showPlan,
  onOpen,
  onPreview,
}: {
  entry: FileEntry;
  showPlan: boolean;
  onOpen: (entry: FileEntry) => void;
  onPreview: (entry: FileEntry) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const title = entry.attachment.title || "이름 없는 파일";
  const source = `${fileSourceCaption(entry)} · ${fileSourceTitle(entry)}`;
  const onKey = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(entry);
    }
  };
  return (
    <tr className={styles.row} tabIndex={0} onClick={() => onOpen(entry)} onKeyDown={onKey} aria-label={`${title}, ${entry.addedBy || "등록한 사람 없음"} 열기`}>
      <td className={mergeClasses(table.td, styles.nameCol)}>
        <span className={styles.nameInner}>
          {entry.kind === "image" ? (
            <button type="button" className={styles.thumbButton} aria-label={`${title} 크게 보기`} onClick={(event) => { event.stopPropagation(); onPreview(entry); }}>
              <LazyThumb src={entry.attachment.url} className={styles.thumbImg} />
            </button>
          ) : (
            <KindIcon kind={entry.kind} />
          )}
          <span className={table.clip} title={title}>
            {title}
          </span>
        </span>
      </td>
      <td className={mergeClasses(table.td, styles.metric, styles.sourceCol)} title={source}>
        <span className={table.clip}>{source}</span>
      </td>
      <td className={mergeClasses(table.td, styles.metric, styles.authorCol, !entry.addedBy && styles.muted)} title={entry.addedBy || undefined}>
        {entry.addedBy ? <AssigneeAvatars names={[entry.addedBy]} max={1} /> : "—"}
      </td>
      {showPlan && (
        <td className={mergeClasses(table.td, styles.metric, styles.planCol)} title={entry.projectName}>
          <span className={table.clip}>{entry.projectName}</span>
        </td>
      )}
      <td className={mergeClasses(table.td, styles.metric, table.num, styles.dateCol, styles.muted)} title={formatDate(entry.addedAt, "numericDateTime")}>
        {formatDate(entry.addedAt, "numeric")}
      </td>
      <td className={mergeClasses(table.td, styles.metric, table.num, styles.sizeCol, !entry.attachment.size && styles.muted)}>{formatBytes(entry.attachment.size) || "—"}</td>
    </tr>
  );
}

function ImageCard({
  entry,
  showPlan,
  onOpen,
  onPreview,
}: {
  entry: FileEntry;
  showPlan: boolean;
  onOpen: (entry: FileEntry) => void;
  onPreview: (entry: FileEntry) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const title = entry.attachment.title || "이름 없는 파일";
  const source = [showPlan ? entry.projectName : "", fileSourceCaption(entry), fileSourceTitle(entry)].filter(Boolean).join(" · ");
  return (
    <div className={styles.card}>
      <button type="button" className={styles.cardImage} aria-label={`${title} 크게 보기`} onClick={() => onPreview(entry)}>
        <LazyThumb src={entry.attachment.url} className={styles.cardImg} />
      </button>
      <button type="button" className={styles.cardMeta} onClick={() => onOpen(entry)} title={`${title}\n${[entry.addedBy, source].filter(Boolean).join(" · ")}`}>
        <span className={table.clip}>{title}</span>
        <span className={styles.cardCaption}>
          {entry.addedBy && <AssigneeAvatars names={[entry.addedBy]} size={16} max={1} />}
          <Caption1 className={mergeClasses(table.role, table.clip)}>{source}</Caption1>
        </span>
      </button>
    </div>
  );
}

function ImageLightbox({ entry, onClose }: { entry: FileEntry; onClose: () => void }) {
  const styles = useStyles();
  const title = entry.attachment.title || "이름 없는 파일";
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogSurface>
        <DialogTitle action={<FuiButton appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} aria-label="닫기" />}>{title}</DialogTitle>
        <DialogContent style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: tokens.spacingVerticalM }}>
          <img src={entry.attachment.url} alt={title} className={styles.lightboxImg} />
        </DialogContent>
        <DialogActions>
          <FuiButton as="a" href={entry.attachment.url} download={title} onClick={desktopDownloadClick(title, entry.attachment.url)} appearance="outline" icon={<ArrowDownload16Regular />}>
            다운로드
          </FuiButton>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

function KindIcon({ kind }: { kind: FileKind }) {
  const color = tokens.colorNeutralForeground3;
  if (kind === "link") return <Link16Regular style={{ color, flexShrink: 0 }} />;
  if (kind === "image") return <Image16Regular style={{ color, flexShrink: 0 }} />;
  return <Document16Regular style={{ color, flexShrink: 0 }} />;
}

function LazyThumb({ src, className }: { src: string; className: string }) {
  return <img src={src} alt="" className={className} loading="lazy" decoding="async" />;
}
